import type { AccountInfo, PublicClientApplication } from '@azure/msal-browser';

import { INAPP_BROWSER_TOOLBAR_COLOR, MSAL_CONFIG } from '@/constants';
import {
  generateCodeChallenge,
  generateRandomString,
} from '@/helper/auth/crypto';
import { buildAuthUrl } from '@/helper/auth/oauth-url';
import type { CodeExchangeResult } from './token-exchange';

/**
 * Native authentication strategy using redirect flow with PKCE
 */
export class NativeAuthStrategy {
  constructor(
    private setUser: (
      account: AccountInfo,
      accessToken?: string,
      expiresOn?: Date,
    ) => void,
    private initLocaleStorage: () => Promise<void>,
    private redirectAfterLogin: () => Promise<void>,
    private exchangeCodeForTokens: (
      code: string,
      codeVerifier: string,
      msalInstance: PublicClientApplication,
      getRedirectUri: () => string,
      msalConfig: { auth: { authority: string; clientId: string } },
    ) => Promise<CodeExchangeResult>,
  ) {}

  async login(
    msalInstance: PublicClientApplication,
    getRedirectUri: () => string,
    msalConfig: { auth: { authority: string; clientId: string } },
  ): Promise<void> {
    const authority = msalConfig.auth.authority;
    const clientId = msalConfig.auth.clientId;
    const redirectUri = getRedirectUri();

    // Generate state and nonce for security
    const state = generateRandomString(
      MSAL_CONFIG.PKCE.RANDOM_STRING_LENGTHS.STATE,
    );
    const nonce = generateRandomString(
      MSAL_CONFIG.PKCE.RANDOM_STRING_LENGTHS.NONCE,
    );

    // Generate PKCE parameters (required for public client flows)
    const codeVerifier = generateRandomString(
      MSAL_CONFIG.PKCE.RANDOM_STRING_LENGTHS.CODE_VERIFIER,
    );
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Store state, nonce, and code verifier in sessionStorage for validation later
    sessionStorage.setItem(MSAL_CONFIG.STORAGE_KEYS.LOGIN_STATE, state);
    sessionStorage.setItem(MSAL_CONFIG.STORAGE_KEYS.LOGIN_NONCE, nonce);
    sessionStorage.setItem(
      MSAL_CONFIG.STORAGE_KEYS.LOGIN_CODE_VERIFIER,
      codeVerifier,
    );

    // Construct the authorization URL with PKCE
    const authUrl = buildAuthUrl(
      authority,
      clientId,
      redirectUri,
      state,
      nonce,
      codeChallenge,
    );

    const { Browser } = await import('@capacitor/browser');
    await Browser.open({
      url: authUrl,
      toolbarColor: INAPP_BROWSER_TOOLBAR_COLOR,
    });
  }

  async logout(
    msalInstance: PublicClientApplication,
    getRedirectUri: () => string,
  ): Promise<void> {
    const { Browser } = await import('@capacitor/browser');
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: getRedirectUri(),
      onRedirectNavigate: (url: string) => {
        void Browser.open({
          url,
          toolbarColor: INAPP_BROWSER_TOOLBAR_COLOR,
        });
        return false;
      },
    });
  }
}
